import { RadioGroupField } from '@monorepo/shared/componentsV2/fields/RadioGroupField';
import {
  InfoCard,
  InfoCardVariant,
} from '@monorepo/shared/componentsV2/InfoCard';
import { Anchor } from '@monorepo/shared/componentsV2/Link';
import { RadioGroupOption } from '@monorepo/shared/componentsV2/RadioGroup';
import { edpJoinTypesProductGuideUrl } from '@monorepo/shared/externalLinks/productGuide';
import { useDataset } from '@monorepo/shared/hooks/datasets/useDataset';
import { isRequired as isRequiredValidator } from '@monorepo/shared/utils/validators';
import { DatasetResponse, JoinType, WorkflowResponse } from 'mapistry-shared';
import React, { useMemo } from 'react';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import { findStepInputDataset } from '../stepUtils';
import {
  getDefaultJoinType,
  isFullOuterJoinAllowed,
  isLeftOuterJoinAllowed,
} from './helpers';
import { StyledDescription, StyledH3 } from './styled';

const StyledInfoCard = styled(InfoCard)`
  max-width: 32rem;
`;

function getJoinTypeOptions({
  stepInputDataset,
  joinToDataset,
  joinToDatasetCustomName,
  workflow,
  stepId,
}: {
  stepInputDataset: DatasetResponse;
  joinToDataset: DatasetResponse | undefined;
  joinToDatasetCustomName: string | undefined;
  workflow: WorkflowResponse;
  stepId: string | undefined;
}): RadioGroupOption[] {
  const dataSource1Name =
    workflow.steps.length === 0 ||
    workflow.steps.findIndex((step) => step.id === stepId) === 0
      ? workflow.inputDataset.name
      : "this Workflow's previous steps";
  const dataSource2Name = joinToDatasetCustomName || 'data source 2';
  return [
    {
      supertitle: 'Inner Join',
      label: 'Only include matching rows',
      value: JoinType.INNER,
    },
    {
      supertitle: 'Full Outer Join',
      label: 'Include all rows',
      value: JoinType.FULL_OUTER,
      disabled:
        !joinToDataset ||
        !isFullOuterJoinAllowed(stepInputDataset, joinToDataset),
    },
    {
      supertitle: 'Left Outer Join',
      label: (
        <>
          Include all rows in <b>{dataSource1Name}</b>, but only matching rows
          from <b>{dataSource2Name}</b>
        </>
      ),
      value: JoinType.LEFT_OUTER,
      disabled:
        !joinToDataset ||
        !isLeftOuterJoinAllowed(stepInputDataset, joinToDataset),
    },
  ];
}

interface JoinTypeModalPageProps {
  workflow: WorkflowResponse;
  stepId?: string;
}

export function JoinTypeModalPage({
  stepId,
  workflow,
}: JoinTypeModalPageProps) {
  const {
    input: { value: selectedJoinToDatasetId },
  } = useField<string>('joinToDatasetId');
  const {
    input: { value: joinToDatasetCustomName },
  } = useField<string>('joinToPrefix');

  const { dataset: joinToDataset } = useDataset({
    organizationId: workflow.organizationId,
    datasetId: selectedJoinToDatasetId,
  });
  const stepInputDataset = useMemo(
    () => findStepInputDataset(workflow, stepId),
    [stepId, workflow],
  );

  const joinTypeOptions: RadioGroupOption[] = useMemo(
    () =>
      getJoinTypeOptions({
        stepInputDataset,
        joinToDataset,
        joinToDatasetCustomName,
        workflow,
        stepId,
      }),
    [
      stepInputDataset,
      joinToDataset,
      joinToDatasetCustomName,
      workflow,
      stepId,
    ],
  );

  const initialDefaultJoinType = joinToDataset
    ? getDefaultJoinType(stepInputDataset, joinToDataset)
    : undefined;

  return (
    <>
      <StyledH3>Choose connection type</StyledH3>
      <StyledDescription>
        {`Choose how you'd like to connect the rows from your combined data sources. `}
        <Anchor href={edpJoinTypesProductGuideUrl} target="_blank">
          Not sure?
        </Anchor>
      </StyledDescription>
      <RadioGroupField
        isLabelHidden
        label="Join Rows"
        name="joinType"
        options={joinTypeOptions}
        required
        validate={isRequiredValidator}
        variant="outlined"
        initialValue={initialDefaultJoinType}
      />
      {joinTypeOptions.some((opt) => opt.disabled) && (
        <StyledInfoCard variant={InfoCardVariant.INFO}>
          When connecting a time-based data source to a non-time-based data
          source, the option to include all rows (aka full outer join) is
          disabled.
        </StyledInfoCard>
      )}
    </>
  );
}
